import type { UnitCategory } from "verdi-common/Configurations/SensorConfigurations/SensorConfigurationTypes";
import type { Unit } from "verdi-common/Configurations/Units/UnitConfigurations";
import type { UnitPresetKey } from "verdi-common/Configurations/Units/UnitPresetsConfigurations";
import { secondMs } from "verdi-common/timeConstants";
import { ApiError } from "@/api/orval/api-error";
import {
    type ExportDataStreamsCsvBody,
    ExportDataStreamsCsvBodyDateTimeFormat,
    ExportDataStreamsCsvBodyHeaderRowsItem,
} from "@/api/orval/generated/schemas";

// Coarser export buckets keep the last value, not the chart's per-stream aggregate, so they would not match the chart.
const EXPORT_RESOLUTION_MS = secondMs;

const HEADER_ROWS = [
    ExportDataStreamsCsvBodyHeaderRowsItem.DEVICE_NAME,
    ExportDataStreamsCsvBodyHeaderRowsItem.UNIT_CATEGORY,
    ExportDataStreamsCsvBodyHeaderRowsItem.DEPTH,
    ExportDataStreamsCsvBodyHeaderRowsItem.UNIT,
];

export type CsvExportView = {
    streamIds: number[];
    startMs: number;
    endMs: number;
    unitPresetKey: UnitPresetKey;
    unitOverrides: Partial<Record<UnitCategory, Unit>>;
    timeZone: string;
};

export function buildCsvExportBody(view: CsvExportView): ExportDataStreamsCsvBody {
    return {
        streamIds: view.streamIds,
        timeRange: {
            start: new Date(view.startMs),
            end: new Date(view.endMs),
            startInclusive: true,
            endInclusive: true,
        },
        resolutionMs: EXPORT_RESOLUTION_MS,
        gapFill: { enabled: false },
        splitByFragment: false,
        timeZone: view.timeZone,
        dateTimeFormat: ExportDataStreamsCsvBodyDateTimeFormat.SPREADSHEET_24H,
        headerRows: HEADER_ROWS,
        units: { preset: view.unitPresetKey, overrides: view.unitOverrides },
        roundValues: true,
    };
}

const fileTimestamp = (ms: number) =>
    new Date(ms)
        .toISOString()
        .replace(/\.\d{3}Z$/, "Z")
        .replaceAll(":", "");

/** Same name the server puts in Content-Disposition, which the generated client does not expose. */
export function csvExportFilename(startMs: number, endMs: number): string {
    return `data-streams-${fileTimestamp(startMs)}-${fileTimestamp(endMs)}.csv`;
}

export type CsvExportErrorKind =
    | "stream-not-found"
    | "too-large"
    | "timeout"
    | "rate-limited"
    | "busy"
    | "unknown-time-zone"
    | "rejected"
    | "internal"
    | "network"
    | "failed";

/** The server sends each error code with its own status. If two codes ever share one, match on `code` instead. */
const KIND_BY_STATUS: Record<number, CsvExportErrorKind> = {
    404: "stream-not-found",
    422: "too-large",
    429: "rate-limited",
    500: "internal",
    503: "busy",
    504: "timeout",
};

const UNKNOWN_TIME_ZONE_MESSAGE = "Unknown time zone";

export function csvExportErrorKind(error: unknown): CsvExportErrorKind {
    if (error instanceof TypeError || (error instanceof DOMException && error.name === "TimeoutError")) {
        return "network";
    }
    if (!(error instanceof ApiError)) return "failed";
    if (error.status === 400) {
        return error.data.message === UNKNOWN_TIME_ZONE_MESSAGE ? "unknown-time-zone" : "rejected";
    }
    return KIND_BY_STATUS[error.status] ?? "failed";
}
